'use client';

import { Pagination } from '@vk-cloud-solutions/marketing-ui';
import { useEffect, useRef, useState } from 'react';
import { useBreakpoints } from 'src/hooks/useBreakpoints';

import { ArticleCardProps } from '~components/ArticleCard/ArticleCard.types';
import { ArticleCardAsymmetricProps } from '~components/ArticleCardAsymmetric/ArticleCardAsymmetric.types';
import { LayoutContainer } from '~components/LayoutContainer/LayoutContainer';
import { useTheme } from '~hooks/useTheme';
import { ThemesNames } from '~types/Theme.types';

import styles from './CategoryPage.module.scss';
import { CategoryPageProps } from './CategoryPage.types';
import { CategoryPageContent } from './CategoryPageContent/CategoryPageContent';
import { CategoryPageNews } from './CategoryPageNews/CategoryPageNews';

const PAGE_SIZE_DESKTOP = 12;
const PAGE_SIZE_MOBILE = 6;
const PAGE_SMALL_CARDS_SIZE_MOBILE = 8;

export const CategoryPage = ({
  category,
  articles,
  sectionEvent,
  isSmallCards,
}: CategoryPageProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [, { isDesktop }] = useBreakpoints();
  const [cards, setCards] = useState<
    (ArticleCardProps | ArticleCardAsymmetricProps)[] | undefined
  >(articles);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSizeMobile = isSmallCards
    ? PAGE_SMALL_CARDS_SIZE_MOBILE
    : PAGE_SIZE_MOBILE;
  const pageSize = isDesktop ? PAGE_SIZE_DESKTOP : pageSizeMobile;

  const { setTheme } = useTheme();

  useEffect(() => {
    setTheme(ThemesNames.White);
  }, [setTheme]);

  const onPageChange = (nextPage: number) => {
    setCurrentPage(nextPage);

    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const newCards = articles?.slice(
      pageSize * (currentPage - 1),
      pageSize * currentPage,
    );
    setCards(newCards);
  }, [articles, currentPage, pageSize]);

  return (
    <LayoutContainer>
      {cards?.length && (
        <div className={styles.container}>
          <div ref={ref}></div>
          {isSmallCards ? (
            <CategoryPageNews
              articles={cards}
              sectionEvent={sectionEvent}
              category={category}
            />
          ) : (
            <CategoryPageContent
              articles={cards}
              sectionEvent={sectionEvent}
              category={category}
            />
          )}
          <Pagination
            totalCount={articles?.length || 0}
            currentPage={currentPage}
            onPageChange={(nextPage) => onPageChange(nextPage)}
            pageSize={pageSize}
            className={styles.pagination}
          />
        </div>
      )}
    </LayoutContainer>
  );
};
